import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import Grid from '@mui/material/Grid2'

const BarInnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
`

const BarValue = styled.div<{ width: number }>`
  height: 1.115rem;
  border-radius: 0.25rem;
  width: ${({ width }) => width}%;
  background-color: ${({ theme }) => theme.palette.secondary.purple400};
`

const TextContainer = styled(Text)`
  font-weight: 300;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`

const Bar = ({
  name,
  value,
  width,
  showDollarSign = false,
}: {
  name: string
  value: number
  width: number
  showDollarSign?: boolean
}) => (
  <Grid container spacing={4} marginBottom={2}>
    <Grid size={{ xs: 4 }}>
      <TextContainer variant='action2'>{name}</TextContainer>
    </Grid>
    <Grid size={{ xs: 8 }}>
      <BarInnerContainer>
        <BarValue width={width} />
        <Text variant='action2' fontWeight={500} marginBottom={0.5}>
          {showDollarSign ? '$' : ''}
          {value.toLocaleString()}
        </Text>
      </BarInnerContainer>
    </Grid>
  </Grid>
)

interface ServicesUsedChartProps {
  services: {
    merchantName?: string
    name?: string
    spend?: number
    usage?: number
    numTrips?: number
  }[]
  showDollarSign?: boolean
}

const ServicesUsedChart = ({
  services = [],
  showDollarSign = false,
  ...props
}: ServicesUsedChartProps) => {
  if (!services || services.length === 0) return null

  // Showing the first 7 services
  const servicesToShow = services.slice(0, 7)

  // Normalize data - support multiple formats
  const normalizedServices = servicesToShow.map((s) => ({
    name: s.merchantName || s.name || 'Unknown',
    value: s.spend || s.usage || s.numTrips || 0,
  }))

  // The width will be percentile from the max value
  const maxValue = () => {
    if (normalizedServices.length === 0) return 100
    return Math.max(...normalizedServices.map((s) => s.value))
  }

  return (
    <div {...props}>
      {normalizedServices.map((service, index) => (
        <Bar
          key={index}
          name={service.name}
          value={service.value}
          width={(service.value / maxValue()) * 100}
          showDollarSign={showDollarSign}
        />
      ))}
    </div>
  )
}

export default ServicesUsedChart
